import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Trash2, ShoppingBasket, ArrowRight, FileText } from "lucide-react";
import { useCart } from "@/context/CartContext";

export default function CartDrawer() {
  const { open, setOpen, items, setQty, remove, count } = useCart();
  const navigate = useNavigate();

  const checkout = () => {
    setOpen(false);
    navigate("/quote");
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[80]" data-testid="cart-drawer">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            data-testid="cart-drawer-backdrop"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 32 }}
            className="absolute right-0 top-0 h-full w-full max-w-md bg-kraft text-coal flex flex-col shadow-2xl"
          >
            <div className="flex items-center justify-between px-6 h-[72px] border-b border-coal/10">
              <div className="flex items-center gap-3">
                <ShoppingBasket size={18} className="text-pine" />
                <h2 className="font-display font-semibold text-xl">Quote cart</h2>
                <span className="font-mono text-[10px] tracking-[0.2em] text-coal/50 uppercase">
                  {count} item{count === 1 ? "" : "s"}
                </span>
              </div>
              <button onClick={() => setOpen(false)} data-testid="cart-drawer-close" className="p-2 hover:text-pine transition-colors">
                <X size={20} />
              </button>
            </div>

            {items.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-4 px-8 text-center" data-testid="cart-empty-state">
                <div className="w-16 h-16 rounded-2xl border-2 border-dashed border-coal/25 grid place-items-center">
                  <FileText size={24} className="text-coal/40" />
                </div>
                <p className="font-display font-medium text-lg">Your quote cart is empty</p>
                <p className="text-sm text-coal/55">
                  Browse the shop and add boxes — we quote every order individually.
                </p>
                <button
                  onClick={() => { setOpen(false); navigate("/shop"); }}
                  data-testid="cart-empty-browse"
                  className="mt-2 rounded-full bg-forest text-kraft font-semibold px-6 h-11 hover:bg-pine transition-colors"
                >
                  Browse the shop
                </button>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4" data-testid="cart-items-list">
                  {items.map((it) => (
                    <div key={it.key} className="flex gap-4 rounded-xl border border-coal/10 bg-white/50 p-3" data-testid="cart-item">
                      <img src={it.image} alt={it.name} className="w-20 h-20 rounded-lg object-cover shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm leading-snug truncate">{it.name}</p>
                        {it.spec && <p className="font-mono text-[10px] text-coal/55 mt-0.5 truncate">{it.spec}</p>}
                        <div className="flex items-center gap-3 mt-2">
                          <div className="flex items-center border border-coal/20 rounded-full">
                            <button onClick={() => setQty(it.key, it.qty - 1)} data-testid="cart-qty-minus" className="px-2.5 h-7 grid place-items-center hover:text-pine">
                              <Minus size={12} />
                            </button>
                            <span className="w-7 text-center font-mono text-xs" data-testid="cart-qty-value">{it.qty}</span>
                            <button onClick={() => setQty(it.key, it.qty + 1)} data-testid="cart-qty-plus" className="px-2.5 h-7 grid place-items-center hover:text-pine">
                              <Plus size={12} />
                            </button>
                          </div>
                          <button onClick={() => remove(it.key)} data-testid="cart-item-remove" className="text-coal/40 hover:text-red-600 transition-colors">
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-coal/10 px-6 py-5 space-y-3">
                  <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-coal/50 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-pine" />
                    No payment now — we quote every box first
                  </p>
                  <button
                    onClick={checkout}
                    data-testid="cart-checkout-btn"
                    className="w-full rounded-full bg-forest text-kraft font-semibold h-12 flex items-center justify-center gap-2 hover:bg-pine transition-colors"
                  >
                    Request a quote
                    <ArrowRight size={17} />
                  </button>
                </div>
              </>
            )}
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );
}
